"use client"

import { useState, useMemo, useCallback, useTransition, useEffect } from "react"
import { Box, Text, Group, Badge, Tabs, SegmentedControl, Alert, Tooltip, ActionIcon, Button, Modal, Stack, Popover, Checkbox } from "@forestsync/ui"
import { notify } from "@forestsync/ui"
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from "react-resizable-panels"
import { useTranslations } from "next-intl"
import { useSearchParams } from "next/navigation"
import { BookmapHeatmap } from "./BookmapHeatmap"
import { OrderBook } from "./OrderBook"
import { OrderTicket } from "./OrderTicket"
import { BuyerModePlacementPanel } from "./BuyerModePlacementPanel"
import { DepthFeedPanel } from "./DepthFeedPanel"
import { ForwardCurvesPanel } from "./ForwardCurvesPanel"
import { OptionChainPanel } from "./OptionChainPanel"
import { OrderConfirmModal } from "./OrderConfirmModal"
import { submitTradeOrder, cancelTradeOrder } from "@/actions/trading"
import { calculateSecondaryCommission } from "@forestsync/lib/commission"
import { TRADE_ORDER_SIDE, TRADE_ORDER_TYPE, TRADE_FILL_TYPE } from "@forestsync/db"
import { useAnalytics } from "@/components/analytics/AnalyticsProvider"
import { TradingProfileModal } from "./TradingProfileModal"
import { DepositFundsModal } from "./DepositFundsModal"
import { getTraderFundingInfoAction } from "@/actions/trade-payment"
import { TbPlus } from "react-icons/tb"
import { TbX, TbExternalLink, TbSettings, TbLayoutSidebarLeftCollapse, TbLayoutSidebarLeftExpand, TbChartGridDots, TbChartCandle, TbChartLine } from "react-icons/tb"
import { MarketChart } from "./MarketChart"
import { ModifyOrderModal } from "./ModifyOrderModal"
import { SessionStatus } from "./SessionStatus"
import { OrderTransmissionCard, type OrderTransmissionResult } from "./OrderTransmissionCard"
import { TradeWatchPanel } from "./TradeWatchPanel"
import { useSidebar } from "@/components/layout/SidebarContext"
import { useMarketChartState, ALL_TIMEFRAMES, OB_PRECISION_OPTIONS } from "@/hooks/useMarketChartState"
import type { OrderBook as OrderBookData, TradeHistoryRow, UserOpenOrder, Market24hStats } from "@/lib/queries"

const REGISTRY_URL = process.env.NEXT_PUBLIC_REGISTRY_URL ?? "http://localhost:4100"

interface TradingTerminalProps {
  creditType: string
  vintage: number
  projectRefId: string
  projectName: string
  creditClass?: 1 | 2
  initialOrderBook: OrderBookData
  initialTradeHistory: TradeHistoryRow[]
  openOrders: UserOpenOrder[]
  initialPrice: number | null
  initialChange: number | null
  stats24h?: Market24hStats | null
  hasStripeAccount?: boolean
  hasStripeCustomer?: boolean
  kycApproved?: boolean
  marketPrices?: Record<string, number>
}

function ResizeHandle() {
  return (
    <PanelResizeHandle>
      <Box
        style={{
          width: 4,
          height: "100%",
          backgroundColor: "var(--mantine-color-default-border)",
          cursor: "col-resize",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box style={{ width: 2, height: 32, borderRadius: 2, backgroundColor: "var(--mantine-color-dimmed)" }} />
      </Box>
    </PanelResizeHandle>
  );
}

function HorizontalResizeHandle() {
  return (
    <PanelResizeHandle>
      <Box
        style={{
          height: 4,
          width: "100%",
          backgroundColor: "var(--mantine-color-default-border)",
          cursor: "row-resize",
        }}
      />
    </PanelResizeHandle>
  );
}

export function TradingTerminal({
  creditType,
  vintage,
  projectRefId,
  projectName,
  initialOrderBook,
  initialTradeHistory,
  openOrders,
  initialPrice,
  initialChange,
  stats24h,
  hasStripeAccount,
  hasStripeCustomer,
  kycApproved,
  marketPrices,
  creditClass = 2,
}: TradingTerminalProps) {
  const t = useTranslations("trading")
  const tNav = useTranslations("nav")
  const { trackEvent } = useAnalytics()
  const { collapsed: sidebarCollapsed, toggle: toggleSidebar } = useSidebar()
  const searchParams = useSearchParams()

  // Sync URL params without RSC re-fetch so sidebar can auto-expand on direct /market load
  useEffect(() => {
    if (searchParams.get("creditType")) return
    const url = `/market?creditType=${encodeURIComponent(creditType)}&vintage=${vintage}&projectRefId=${encodeURIComponent(projectRefId)}`
    window.history.replaceState(null, "", url)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    obPrecision, setObPrecision,
    chartType, setChartType,
    timeframe, timeframePending,
    showVwap, setShowVwap,
    showVolumeProfile, setShowVolumeProfile,
    visibleTimeframes,
    fillsLoading,
    chartFills,
    hoveredOhlcv, setHoveredOhlcv,
    orderBook,
    recentFills,
    livePrice,
    liveStats,
    liveChange,
    priceUp,
    ohlcvBars,
    blockTradeMarkers,
    vwapOverlay,
    handleOrderFilled,
    handleTimeframeChange,
    handleVisibleTimeframesChange,
  } = useMarketChartState({
    creditType, vintage, projectRefId,
    initialOrderBook, initialTradeHistory, initialPrice, initialChange, stats24h,
  })

  const [orderResult, setOrderResult] = useState<OrderTransmissionResult | null>(null)
  const [quickOrderPending] = useTransition()
  const [quickOrderError, setQuickOrderError] = useState<string | null>(null)
  const [quickOrderModal, setQuickOrderModal] = useState<{
    input: Parameters<typeof submitTradeOrder>[0]
    breakdown: { subtotal: number; platformFee: number; totalDue: number }
  } | null>(null)
  const [profileModalOpen, setProfileModalOpen] = useState(false)

  const [balance, setBalance] = useState<number | null>(null)
  const [fundingInstructions, setFundingInstructions] = useState<{
    iban: string
    bic: string
    accountHolder: string
    reference: string
  } | null>(null)
  const [depositModalOpen, setDepositModalOpen] = useState(false)

  const fetchFundingInfo = useCallback(() => {
    getTraderFundingInfoAction().then(res => {
      if ("balanceCents" in res) {
        setBalance(res.balanceCents / 100)
        setFundingInstructions(res.fundingInstructions)
      }
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (hasStripeCustomer || hasStripeAccount) {
      fetchFundingInfo()
    }
  }, [hasStripeCustomer, hasStripeAccount, fetchFundingInfo])

  const [placeOrderOpen, setPlaceOrderOpen] = useState(false)

  const [cancelTarget, setCancelTarget] = useState<bigint | null>(null)
  const [cancelPending, startCancelTransition] = useTransition()
  const [modifyTarget, setModifyTarget] = useState<UserOpenOrder | null>(null)

  const marketOrders = useMemo(
    () => openOrders.filter(
      (o) => o.creditType === creditType && o.vintage === vintage && o.projectRefId === projectRefId
    ),
    [openOrders, creditType, vintage, projectRefId]
  )

  function handleCancelOrder() {
    if (!cancelTarget) return
    startCancelTransition(async () => {
      const result = await cancelTradeOrder(cancelTarget)
      if ("error" in result) {
        notify.error(result.error ?? t("orderError"))
      } else {
        notify.success(t("orderCancelled"))
      }
      setCancelTarget(null)
    })
  }

  const handleQuickOrder = useCallback(
    (side: "buy" | "sell", price: number, volume: number) => {
      const orderSide = side === "buy" ? TRADE_ORDER_SIDE.BID : TRADE_ORDER_SIDE.ASK
      const subtotal = price * volume
      const feeResult = calculateSecondaryCommission({ quantity: volume, pricePerTon: price, platformAnnualVolume: 0 })
      const platformFee = feeResult.commission
      setQuickOrderError(null)
      setQuickOrderModal({
        input: {
          creditType,
          vintage,
          projectRefId,
          creditClass: creditClass as 1 | 2,
          side: orderSide,
          orderType: TRADE_ORDER_TYPE.LIMIT,
          price,
          volume,
          fillType: TRADE_FILL_TYPE.PARTIAL,
        },
        breakdown: { subtotal, platformFee, totalDue: subtotal + platformFee },
      })
    },
    [creditType, vintage, projectRefId],
  );

  return (
    <>
      {/* Desktop-only: hide below md (parent page handles fallback) */}
      <Box visibleFrom="md" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Market header - 2 rows */}
      <Stack gap={0} style={{ borderBottom: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}>
        {/* Row 1: Context — sidebar toggle + project identity + session status + registry link */}
        <Group px={8} py={4} gap="sm" wrap="nowrap">
          <Tooltip label={tNav(sidebarCollapsed ? "showSidebar" : "hideSidebar")} position="bottom" withArrow>
            <ActionIcon variant="subtle" size="sm" onClick={toggleSidebar} className="shrink-0">
              {sidebarCollapsed
                ? <TbLayoutSidebarLeftExpand size={15} />
                : <TbLayoutSidebarLeftCollapse size={15} />
              }
            </ActionIcon>
          </Tooltip>

          <Group gap={6} wrap="nowrap" className="shrink-0">
            <Text size="sm" c="dimmed" className="whitespace-nowrap">{projectName}</Text>
            <Tooltip label={t("viewInRegistry")} withArrow position="top">
              <Button
                component="a"
                href={`${REGISTRY_URL}/sink/${projectRefId}`}
                target="_blank"
                rel="noopener noreferrer"
                size="compact-xs"
                variant="subtle"
                rightSection={<TbExternalLink size={12} />}
                className="shrink-0"
              >
                {t("viewTrade")}
              </Button>
            </Tooltip>
            <Badge variant="dot" size="sm" color="blue">{creditType}</Badge>
            <Badge variant="outline" size="xs">{t("vintage")} {vintage}</Badge>
          </Group>

          <Box flex={1} />

          <SessionStatus />

          
        </Group>

        {/* Row 2: Live trading data — price + bid/ask/spread + OHLCV */}
        <Group
          px={8}
          py={3}
          gap="sm"
          wrap="nowrap"
          style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
        >
          {livePrice !== null && (
            <Group gap={6} align="baseline" wrap="nowrap" className="shrink-0">
              <Text size="lg" fw={700} c={priceUp ? "green" : "red"} className="whitespace-nowrap">
                €{livePrice.toFixed(2)}
              </Text>
              {liveChange !== null && (
                <Badge size="sm" color={liveChange >= 0 ? "green" : "red"} variant="light">
                  {liveChange >= 0 ? "+" : ""}{liveChange.toFixed(2)}%
                </Badge>
              )}
            </Group>
          )}

          {orderBook.bids.length > 0 && orderBook.asks.length > 0 && (() => {
            const bestBid = orderBook.bids[0].price
            const bestAsk = orderBook.asks[0].price
            const spread = (bestAsk - bestBid).toFixed(2)
            const spreadPct = ((bestAsk - bestBid) / bestAsk) * 100
            return (
              <Group gap={8} wrap="nowrap" className="shrink-0">
                <Text size="xs" c="dimmed" className="shrink-0">|</Text>
                <Tooltip label={t("bestBid")} position="bottom" withArrow>
                  <Text size="xs" c="green" fw={600} className="whitespace-nowrap">Bid €{bestBid.toFixed(2)}</Text>
                </Tooltip>
                <Tooltip label={t("bestAsk")} position="bottom" withArrow>
                  <Text size="xs" c="red" fw={600} className="whitespace-nowrap">Ask €{bestAsk.toFixed(2)}</Text>
                </Tooltip>
                <Tooltip label={t("spread")} position="bottom" withArrow>
                  <Text size="xs" className="whitespace-nowrap">Spread €{spread} · {spreadPct.toFixed(2)}%</Text>
                </Tooltip>
              </Group>
            )
          })()}

          {/* OHLCV: hovered candle values take priority over 24h stats */}
          {hoveredOhlcv ? (
            <Group gap={8} wrap="nowrap" className="shrink-0">
              <Text size="xs" c="dimmed" className="shrink-0">|</Text>
              <Tooltip label="Open" position="bottom" withArrow>
                <Text size="xs" c="dimmed" className="whitespace-nowrap">O €{hoveredOhlcv.open.toFixed(2)}</Text>
              </Tooltip>
              <Tooltip label="High" position="bottom" withArrow>
                <Text size="xs" c="green" className="whitespace-nowrap">H €{hoveredOhlcv.high.toFixed(2)}</Text>
              </Tooltip>
              <Tooltip label="Low" position="bottom" withArrow>
                <Text size="xs" c="red" className="whitespace-nowrap">L €{hoveredOhlcv.low.toFixed(2)}</Text>
              </Tooltip>
              <Tooltip label="Close" position="bottom" withArrow>
                <Text size="xs" className="whitespace-nowrap">C €{hoveredOhlcv.close.toFixed(2)}</Text>
              </Tooltip>
              <Tooltip label="Volume (tCO₂e)" position="bottom" withArrow>
                <Text size="xs" c="dimmed" className="whitespace-nowrap">V {hoveredOhlcv.volume.toFixed(1)}t</Text>
              </Tooltip>
            </Group>
          ) : (liveStats?.high != null || liveStats?.low != null) ? (
            <Group gap={8} wrap="nowrap" className="shrink-0">
              <Text size="xs" c="dimmed" className="shrink-0">|</Text>
              {liveStats.high != null && (
                <Tooltip label={t("high24h")} position="bottom" withArrow>
                  <Text size="xs" c="green" className="whitespace-nowrap">H €{liveStats.high.toFixed(2)}</Text>
                </Tooltip>
              )}
              {liveStats.low != null && (
                <Tooltip label={t("low24h")} position="bottom" withArrow>
                  <Text size="xs" c="red" className="whitespace-nowrap">L €{liveStats.low.toFixed(2)}</Text>
                </Tooltip>
              )}
              {liveStats.volumeEur > 0 && (
                <Tooltip label={t("volume24hEur")} position="bottom" withArrow>
                  <Text size="xs" c="dimmed" className="whitespace-nowrap">
                    V €{liveStats.volumeEur >= 1000
                      ? `${(liveStats.volumeEur / 1000).toFixed(1)}k`
                      : liveStats.volumeEur.toFixed(0)}
                  </Text>
                </Tooltip>
              )}
            </Group>
          ) : null}
        </Group>
      </Stack>


      <PanelGroup orientation="vertical" style={{ flex: 1, minHeight: 0 }}>
        {/* Top: Chart + Order Book */}
        <Panel defaultSize={72} minSize={40}>
          <PanelGroup orientation="horizontal" style={{ height: "100%" }}>
            {/* Left column: Charts */}
            <Panel defaultSize={65} minSize={30}>
              <Tabs
                defaultValue="bookmap"
                keepMounted={false}
                h="100%"
                display="flex"
                style={{ flexDirection: "column" }}
              >
                <Tabs.List px={8}>
                  <Tabs.Tab value="bookmap">{t("chartTab")}</Tabs.Tab>
                  <Tabs.Tab value="depth">{t("depthFeedTab")}</Tabs.Tab>
                  <Tabs.Tab value="curves">{t("forwardCurvesTab")}</Tabs.Tab>
                  <Tabs.Tab value="options">{t("optionChainTab")}</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="bookmap" flex={1} mih={0} style={{ display: "flex", flexDirection: "column" }}>
                  <Group px={8} py={4} style={{ flexShrink: 0 }} justify="space-between">
                    <Group gap={4} align="center">
                      <Popover position="bottom-start" withArrow>
                        <Popover.Target>
                          <ActionIcon variant="subtle" size="sm" title={t("chartSettings")}>
                            <TbSettings size={14} />
                          </ActionIcon>
                        </Popover.Target>
                      <Popover.Dropdown>
                        <Stack gap={4}>
                          <Text size="xs" fw={600}>{t("chartSettings")}</Text>
                          <Checkbox
                            label={t("showVwap")}
                            size="xs"
                            checked={showVwap}
                            onChange={(e) => setShowVwap(e.currentTarget.checked)}
                          />
                          <Checkbox
                            label={t("showVolumeProfile")}
                            size="xs"
                            checked={showVolumeProfile}
                            onChange={(e) => setShowVolumeProfile(e.currentTarget.checked)}
                          />
                          <Text size="xs" fw={600} mt={4}>{t("visibleTimeframes")}</Text>
                          {ALL_TIMEFRAMES.map((tf) => (
                            <Checkbox
                              key={tf}
                              label={t(`timeframes.${tf}`)}
                              size="xs"
                              checked={visibleTimeframes.includes(tf)}
                              onChange={(e) => handleVisibleTimeframesChange(tf, e.currentTarget.checked)}
                            />
                          ))}
                        </Stack>
                      </Popover.Dropdown>
                      </Popover>
                      {ALL_TIMEFRAMES.filter((tf) => visibleTimeframes.includes(tf)).map((tf) => (
                        <Button
                          key={tf}
                          size="xs"
                          variant={timeframe === tf ? "filled" : "subtle"}
                          loading={timeframePending && timeframe === tf}
                          onClick={() => handleTimeframeChange(tf)}
                          px={6}
                        >
                          {t(`timeframes.${tf}`)}
                        </Button>
                      ))}
                    </Group>
                  <Group gap={2} mr={4}>
                    <ActionIcon size="sm" variant={chartType === "heatmap" ? "filled" : "subtle"} onClick={() => setChartType("heatmap")} title="Bookmap">
                      <TbChartGridDots size={14} />
                    </ActionIcon>
                    <ActionIcon size="sm" variant={chartType === "candlestick" ? "filled" : "subtle"} onClick={() => setChartType("candlestick")} title="Candlestick">
                      <TbChartCandle size={14} />
                    </ActionIcon>
                    <ActionIcon size="sm" variant={chartType === "line" ? "filled" : "subtle"} onClick={() => setChartType("line")} title="Line">
                      <TbChartLine size={14} />
                    </ActionIcon>
                  </Group>
                </Group>
                  <Box style={{ flex: 1, padding: 8, minHeight: 0, position: "relative" }}>
                    {fillsLoading && (
                      <Box
                        className="animate-pulse"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 8,
                          right: 8,
                          height: 2,
                          zIndex: 10,
                          background: "var(--mantine-color-teal-6)",
                          borderRadius: 1,
                        }}
                      />
                    )}
                    {chartType === "heatmap" ? (
                      <BookmapHeatmap
                        orderBook={orderBook}
                        fills={chartFills}
                        precision={obPrecision}
                        timeframe={timeframe}
                        showVwap={showVwap}
                        showVolumeProfile={showVolumeProfile}
                        onCrosshairMove={setHoveredOhlcv}
                      />
                    ) : ohlcvBars.length === 0 ? (
                      <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <Text c="dimmed" size="sm">No trade history</Text>
                      </Box>
                    ) : (
                      <MarketChart
                        type={chartType === "candlestick" ? "candlestick" : "area"}
                        data={ohlcvBars}
                        height="100%"
                        overlays={showVwap && vwapOverlay.length > 0 ? [{ label: "VWAP", color: "var(--mantine-color-yellow-5)", data: vwapOverlay }] : []}
                        markers={chartType === "candlestick" ? blockTradeMarkers : undefined}
                      />
                    )}
                  </Box>
                </Tabs.Panel>

                <Tabs.Panel value="depth" flex={1} style={{ overflowY: "auto" }}>
                  <DepthFeedPanel orderBook={orderBook} recentFills={recentFills} />
                </Tabs.Panel>

                <Tabs.Panel value="curves" flex={1} style={{ overflowY: "auto" }}>
                  <ForwardCurvesPanel spotPrice={livePrice} creditType={creditType} />
                </Tabs.Panel>

                <Tabs.Panel value="options" flex={1} style={{ overflowY: "auto" }}>
                  <OptionChainPanel creditType={creditType} vintage={vintage} projectRefId={projectRefId} />
                </Tabs.Panel>
              </Tabs>
            </Panel>

            <ResizeHandle />

            {/* Right column: Order Book */}
            <Panel defaultSize={35} minSize={25}>
              <Box style={{ height: "100%", padding: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <Group justify="space-between" mb={4} style={{ flexShrink: 0 }} wrap="nowrap">
                  <Text size="sm" fw={600}>{t("orderBook")}</Text>
                  <SegmentedControl
                    size="xs"
                    data={OB_PRECISION_OPTIONS}
                    value={String(obPrecision)}
                    onChange={(v) => setObPrecision(Number(v))}
                  />
                </Group>
                {orderResult && (
                  <OrderTransmissionCard
                    result={orderResult}
                    onDismiss={() => setOrderResult(null)}
                  />
                )}
                {quickOrderError && (
                  <Alert color="red" py={4} mb={4} onClose={() => setQuickOrderError(null)} withCloseButton style={{ flexShrink: 0 }}>
                    <Text size="xs">{quickOrderError}</Text>
                  </Alert>
                )}
                <Box style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                <OrderBook
                  bids={orderBook.bids}
                  asks={orderBook.asks}
                  lastPrice={livePrice ?? undefined}
                  referencePrice={stats24h?.high ?? stats24h?.low ?? undefined}
                  precision={obPrecision}
                  userOrders={marketOrders}
                  onQuickOrder={hasStripeAccount ? handleQuickOrder : undefined}
                  onCancelOrder={(id) => setCancelTarget(id)}
                  isPending={quickOrderPending}
                />
                </Box>
              </Box>
            </Panel>
          </PanelGroup>
        </Panel>
        <HorizontalResizeHandle />
        {/* Bottom: TradeWatch — full width, all markets */}
        <Panel defaultSize={28} minSize={15}>
          <TradeWatchPanel
            openOrders={openOrders}
            marketPrices={marketPrices ?? {}}
            onCancelOrder={(id) => setCancelTarget(id)}
            onModifyOrder={(order) => setModifyTarget(order)}
            onPlaceOrder={() => setPlaceOrderOpen(true)}
          />
        </Panel>
      </PanelGroup>
    </Box>

    <Modal
      opened={placeOrderOpen}
      onClose={() => setPlaceOrderOpen(false)}
      title={t("placeOrder")}
      size="sm"
      centered
    >
      {hasStripeAccount ? (
        <OrderTicket
          creditType={creditType}
          vintage={vintage}
          projectRefId={projectRefId}
          creditClass={creditClass as 1 | 2}
          onOrderFilled={(fill) => { handleOrderFilled(fill); setPlaceOrderOpen(false) }}
          availableBalance={balance}
          onFundAccount={() => { setPlaceOrderOpen(false); setDepositModalOpen(true) }}
          bestAsk={orderBook.asks[0]?.price ?? null}
          bestBid={orderBook.bids[0]?.price ?? null}
        />
      ) : (
        <BuyerModePlacementPanel
          projectRefId={projectRefId}
          creditType={creditType}
          vintage={vintage}
          bestAsk={orderBook.asks[0]?.price ?? null}
          kycApproved={kycApproved ?? false}
        />
      )}
    </Modal>
    <DepositFundsModal opened={depositModalOpen} onClose={() => setDepositModalOpen(false)} fundingInstructions={fundingInstructions} onRetry={fetchFundingInfo} />
    <TradingProfileModal
      opened={profileModalOpen}
      onClose={() => setProfileModalOpen(false)}
      onSuccess={() => setProfileModalOpen(false)}
    />
    <Modal
      opened={cancelTarget !== null}
      onClose={() => setCancelTarget(null)}
      title={t("cancelOrder")}
      size="sm"
      centered
    >
      <Stack gap="md">
        <Text size="sm">{t("cancelOrderConfirm")}</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setCancelTarget(null)}>{t("no")}</Button>
          <Button color="red" loading={cancelPending} onClick={handleCancelOrder}>{t("cancelOrder")}</Button>
        </Group>
      </Stack>
    </Modal>
    <ModifyOrderModal order={modifyTarget} onClose={() => setModifyTarget(null)} />
    <OrderConfirmModal
      opened={quickOrderModal !== null}
      onClose={() => setQuickOrderModal(null)}
      orderInput={quickOrderModal?.input ?? null}
      breakdown={quickOrderModal?.breakdown ?? null}
      onSuccess={(fill) => {
        const input = quickOrderModal?.input
        setQuickOrderModal(null)
        if (fill) handleOrderFilled(fill)
        if (input?.price != null) {
          setOrderResult({
            price: input.price,
            filledVolume: fill?.volume ?? 0,
            totalVolume: input.volume,
            currentAsk: orderBook.asks[0]?.price ?? null,
          })
        }
      }}
      onProfileRequired={() => {
        setQuickOrderModal(null)
        setProfileModalOpen(true)
      }}
    />
    </>
  )
}
